import { apiClient, ApiClient } from './client';
import type { ChatListResponse, ChatDetailResponse, ChatMember, Message, PlayableType, PlayableData, AddMessagePayload } from './types';

class Chat {
  public readonly id: string;
  private readonly apiClient: ApiClient;
  public display_name: string;
  public icon: string;
  public members: ChatMember[];
  public messages: Message[];

  constructor(id: string, apiClient: ApiClient, details: ChatDetailResponse) {
    this.id = id;
    this.apiClient = apiClient;
    this.display_name = details.display_name;
    this.icon = details.icon;
    this.members = details.members;
    this.messages = details.messages;
  }

  async delete(): Promise<void> {
    try {
      await this.apiClient.delete(`/chats/${this.id}/`);
    } catch (error) {
      console.error('Failed to delete chat:', error);
      throw error;
    }
  }

  async addMessage(content: string, persona?: string): Promise<Chat> {
    try {
      const requestBody: AddMessagePayload = { content };
      if (persona) {
        requestBody.persona = persona;
      }

      const response = await this.apiClient.post(`/chats/${this.id}/add-message/`, requestBody);
      return new Chat(this.id, this.apiClient, response.data);
    } catch (error) {
      console.error('Failed to add message:', error);
      throw error;
    }
  }

  async addImageMessage(image: File, content?: string, persona?: string): Promise<Chat> {
    try {
      const formData = new FormData();
      if (content) {
        formData.append('content', content);
      }
      formData.append('attachment', image);
      if (persona) {
        formData.append('persona', persona);
      }

      const response = await this.apiClient.post(
        `/chats/${this.id}/add-attachment-message/`, 
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        }
      );
      return new Chat(this.id, this.apiClient, response.data);
    } catch (error) {
      console.error('Failed to add message with image:', error);
      throw error;
    }
  }

  async generate(playable_type?: PlayableType, playable_data?: PlayableData): Promise<Chat> {
    try {
      const requestBody: { playable_type?: PlayableType; playable_data?: PlayableData } = {};
      
      if (playable_type) {
        requestBody.playable_type = playable_type;
        
        if (playable_data) {
          requestBody.playable_data = playable_data;
        }
      }

      const response = await this.apiClient.post(`/chats/${this.id}/generate/`, requestBody);
      return new Chat(this.id, this.apiClient, response.data);
    } catch (error) {
      console.error('Failed to generate message:', error);
      throw error;
    }
  }

  async setPersona(personaId: string): Promise<Chat> {
    try {
      const response = await this.apiClient.post(`/chats/${this.id}/set-persona/`, { persona: personaId });
      return new Chat(this.id, this.apiClient, response.data);
    } catch (error) {
      console.error('Failed to set persona:', error);
      throw error;
    }
  }

  async generateImage(prompt: string, persona?: string): Promise<Chat> {
    try {
      const requestBody: { prompt: string; persona?: string } = { prompt };
      if (persona) {
        requestBody.persona = persona;
      }

      const response = await this.apiClient.post(`/chats/${this.id}/generate-image/`, requestBody);
      return new Chat(this.id, this.apiClient, response.data);
    } catch (error) {
      console.error('Failed to generate image:', error);
      throw error;
    }
  }

  async shareSpotifyTrack(trackId: string, trackName: string, trackArtists: string[], persona?: string): Promise<Chat> {
    try {
      const requestBody: { track_id: string; track_name: string; track_artist: string; persona?: string } = {
        track_id: trackId,
        track_name: trackName,
        track_artist: trackArtists.length > 1 ? trackArtists.join(", ") : trackArtists[0]
      };
      if (persona) {
        requestBody.persona = persona;
      }

      const response = await this.apiClient.post(`/chats/${this.id}/share-spotify-track/`, requestBody);
      return new Chat(this.id, this.apiClient, response.data);
    } catch (error) {
      console.error('Failed to share Spotify track:', error);
      throw error;
    }
  }

  async rewindToMessage(messageId: string): Promise<Chat> {
    try {
      const response = await this.apiClient.post(`/chats/${this.id}/rewind-to-message/`, { message: messageId });
      return new Chat(this.id, this.apiClient, response.data);
    } catch (error) {
      console.error('Failed to rewind chat:', error);
      throw error;
    }
  }

  async newChatFromMessage(messageId: string): Promise<Chat> {
    try {
      const response = await this.apiClient.post(`/chats/${this.id}/new-chat-from-message/`, { message: messageId });
      return new Chat(response.data.id, this.apiClient, response.data);
    } catch (error) {
      console.error('Failed to create new chat from message:', error);
      throw error;
    }
  }

  async editMessage(messageId: string, content?: string, embeds?: Record<string, any>): Promise<Chat> {
    try {
      const response = await this.apiClient.post(`/chats/${this.id}/edit-message/`, {
        message: messageId,
        content,
        embeds
      });
      
      return new Chat(this.id, this.apiClient, response.data);
    } catch (error) {
      console.error('Failed to edit message:', error);
      throw error;
    }
  }

  async get(): Promise<Chat> {
    try {
      const response = await this.apiClient.get(`/chats/${this.id}/`);
      return new Chat(this.id, this.apiClient, response.data);
    } catch (error) {
      console.error('Failed to fetch chat details:', error);
      throw error;
    }
  }

  async regenerate(messageId: string, playable_type?: PlayableType): Promise<Chat> {
    try {
      const requestBody = { message: messageId };
      
      if (playable_type) {
        Object.assign(requestBody, { playable_type });
      }

      const response = await this.apiClient.post(`/chats/${this.id}/regenerate/`, requestBody);
      return new Chat(this.id, this.apiClient, response.data);
    } catch (error) {
      console.error('Failed to regenerate message:', error);
      throw error;
    }
  }

  async bump(): Promise<void> {
    try {
      await this.apiClient.post(`/chats/${this.id}/bump/`);
    } catch (error) {
      console.error('Failed to bump chat:', error);
      throw error;
    }
  }

  async addSummary(content: string): Promise<Chat> {
    try {
      const response = await this.apiClient.post(`/chats/${this.id}/add-summary/`, { content });
      return new Chat(this.id, this.apiClient, response.data);
    } catch (error) {
      console.error('Failed to add summary:', error);
      throw error;
    }
  }

  async selectRegenerationOption(messageId: string, regenerationNumber: number): Promise<Chat> {
    try {
      const response = await this.apiClient.post(`/chats/${this.id}/select-regeneration-option/`, {
        message: messageId,
        regeneration_number: regenerationNumber
      });
      return new Chat(this.id, this.apiClient, response.data);
    } catch (error) {
      console.error('Failed to select regeneration option:', error);
      throw error;
    }
  }
}

export const chats = {
  async list(): Promise<ChatListResponse[]> {
    try {
      const response = await apiClient.get('/chats/');
      return response.data;
    } catch (error) {
      console.error('Failed to fetch chats:', error);
      throw error;
    }
  },

  async create(characterId: string): Promise<Chat> {
    try {
      const response = await apiClient.post('/chats/', { character: characterId });
      return new Chat(response.data.id, apiClient, response.data);
    } catch (error) {
      console.error('Failed to create chat:', error);
      throw error;
    }
  },

  async get(chatId: string): Promise<Chat> {
    try {
      const response = await apiClient.get(`/chats/${chatId}/`);
      return new Chat(chatId, apiClient, response.data);
    } catch (error) {
      console.error('Failed to fetch chat details:', error);
      throw error;
    }
  }
};

export type { Chat as ChatType };